import { LoopButton, Modal, Tooltip, Typography } from '@loophealth/loop-ui-web-library';
import React from 'react';
import { CompleteFamilyGraphic, FamilyIcon, ShruggingGraphic } from '../../../assets/img';
import { Close, InfoIcon } from '../../../assets/img/CustomIcons';
import ArrowCircleUpIcon from '../../../assets/img/CustomIcons/ArrowCircleUpIcon';
import FamilyOutlinedIcon from '../../../assets/img/CustomIcons/FamilyOutlinedIcon';
import theme from '../../../theme';
import WithThemeProvider from '../../../theme/WithThemeProvider';
import { capitalizeFirstLetter, formatFullName, getIconMapping } from '../../../utils/common';
import { Loader } from '../../atoms';
import ProfileIcon from '../../atoms/ProfileIcon';
import BenefitCard from '../AlaCarteBenefits/BenefitCard';
import BenefitDetailsModal from '../BenefitDetailsModal';
import { StyledClose } from '../ModularFtuxModal/styles';
import useModularAddOns from './hooks/useModularAddOns';
import {
    StyledModularAddOnBanner,
    StyledModularAddOnBannerText,
    StyledModularAddOnFamilyIcon,
    StyledModularAddOnsBody,
    StyledModularAddOnsCloseModal,
    StyledModularAddOnsCloseModalBody,
    StyledModularAddOnsCloseModalFooter,
    StyledModularAddOnsCompletionModalContent,
    StyledModularAddOnsCompletionModalFam,
    StyledModularAddOnsCompletionModalFamNew,
    StyledModularAddOnsCompletionModalFamNewText,
    StyledModularAddOnsCompletionModalFamNewUpgrade,
    StyledModularAddOnsCompletionModalText,
    StyledModularAddOnsCompletionsModalBody,
    StyledModularAddOnsDeletionsModalContent,
    StyledModularAddOnsHeader,
    StyledModularAddOnsModal,
    StyledModularDeletableMember,
    StyledModularDeletableMemberText,
    StyledModularDot
} from './styles';
import { IPlanDeletionDiffMode } from './types';

const ModularAddOnsModal: React.FC = () => {
    const {
        isVisible,
        isCompleteModalVisible,
        isDeleting,
        isDeletionModalVisible,
        isUpgrading,
        isDowngrading,
        selectedAddOns,
        deletionPlan,
        famDefUpgrade,
        readMorePlan,
        addOns,
        setIsVisible,
        onModalClose,
        handleSelect,
        handleRemove,
        handleReadMore,
        closeReadMoreModal,
        handleCloseCompletionModal,
        handleCloseDeletionModal,
        confirmRemoveDependents,
        handleUpdate
    } = useModularAddOns(true);

    const showBanner = addOns.length > 0;
    const hasUpgraded = selectedAddOns.length > 0;
    return (
        <>
            {showBanner && (
                <StyledModularAddOnBanner>
                    <StyledModularAddOnBannerText>
                        <Typography variant="small" weight="semiBold" color="tertiary">
                            {hasUpgraded ? 'Your Family Structure Is Now Upgraded 👍' : 'Make Room for More Family✨'}
                        </Typography>
                        <StyledModularDot />
                        <Typography variant="extraSmall" weight="medium" color="tertiary">
                            {hasUpgraded
                                ? 'Benefit added! You can now manage your upgraded family structure.'
                                : `You're eligible to include more family members through available upgrades.`}
                        </Typography>
                    </StyledModularAddOnBannerText>
                    {hasUpgraded ? (
                        <Tooltip
                            beak="top-right"
                            text="You can manage your selected family structure plan here."
                            type="informative"
                        >
                            <LoopButton
                                variant="outline"
                                size="small"
                                onClick={() => setIsVisible(true)}
                                width="fit-content"
                                textColor="tertiary"
                            >
                                Manage Family Structure
                            </LoopButton>
                        </Tooltip>
                    ) : (
                        <LoopButton
                            variant="filled"
                            size="small"
                            onClick={() => setIsVisible(true)}
                            width="fit-content"
                        >
                            Upgrade Now
                        </LoopButton>
                    )}
                </StyledModularAddOnBanner>
            )}
            <Modal
                isVisible={isVisible}
                setIsVisible={onModalClose}
                isModalClosable={false}
                disablePadding
                overflow="visible"
            >
                <WithThemeProvider>
                    <StyledClose onClick={onModalClose}>
                        <Close />
                    </StyledClose>
                    <StyledModularAddOnsModal>
                        <StyledModularAddOnsHeader>
                            <StyledModularAddOnFamilyIcon src={FamilyIcon} alt="Family Icon" />
                            <Typography variant="large" weight="semiBold" color="tertiary">
                                Make Room for More Family✨
                            </Typography>
                            <Typography variant="small" weight="medium" color="tertiary">
                                Based on your current plan, you&apos;re eligible to include more family members through
                                available upgrades.
                            </Typography>
                        </StyledModularAddOnsHeader>
                        <StyledModularAddOnsBody>
                            <Typography variant="small">Choose a structure that fits your family best.</Typography>
                            {addOns.map((a) => (
                                <BenefitCard
                                    key={a.id}
                                    plan={a}
                                    selectedBenefits={selectedAddOns}
                                    handleReadMore={handleReadMore}
                                    handleSelectPlan={handleSelect}
                                    handleRemovePlan={handleRemove}
                                    handleUpdate={handleUpdate}
                                />
                            ))}
                        </StyledModularAddOnsBody>
                    </StyledModularAddOnsModal>
                </WithThemeProvider>
            </Modal>
            <Modal
                isVisible={isCompleteModalVisible}
                setIsVisible={handleCloseCompletionModal}
                isModalClosable={false}
                disablePadding
            >
                <WithThemeProvider>
                    {!isUpgrading && (
                        <StyledClose onClick={handleCloseCompletionModal}>
                            <Close />
                        </StyledClose>
                    )}
                    <StyledModularAddOnsCloseModal>
                        <img src={CompleteFamilyGraphic} />
                        {isUpgrading ? (
                            <Loader minHeight="100px" />
                        ) : (
                            <StyledModularAddOnsCompletionsModalBody>
                                <StyledModularAddOnsCompletionModalText>
                                    <Typography variant="title2" weight="medium">
                                        Congratulations!
                                    </Typography>
                                    <Typography variant="medium" weight="medium">
                                        Your Family Structure Has Been Upgraded
                                    </Typography>
                                </StyledModularAddOnsCompletionModalText>
                                <StyledModularAddOnsCompletionModalContent>
                                    <Typography variant="small" weight="medium" color="secondary">
                                        Previous Family Structure
                                    </Typography>
                                    <StyledModularAddOnsCompletionModalFam>
                                        <FamilyOutlinedIcon />
                                        <Typography variant="extraSmall" weight="medium">
                                            {famDefUpgrade?.prev}
                                        </Typography>
                                    </StyledModularAddOnsCompletionModalFam>
                                    <Typography variant="small" weight="medium" color="secondary">
                                        New Family Structure
                                    </Typography>
                                    <StyledModularAddOnsCompletionModalFamNew>
                                        <StyledModularAddOnsCompletionModalFamNewText>
                                            <FamilyOutlinedIcon />
                                            <Typography variant="extraSmall" weight="medium">
                                                {famDefUpgrade?.new}
                                            </Typography>
                                        </StyledModularAddOnsCompletionModalFamNewText>
                                        <StyledModularAddOnsCompletionModalFamNewUpgrade>
                                            <ArrowCircleUpIcon />
                                            <Typography variant="small" weight="medium" color="emerald">
                                                Upgraded
                                            </Typography>
                                        </StyledModularAddOnsCompletionModalFamNewUpgrade>
                                    </StyledModularAddOnsCompletionModalFamNew>
                                    <LoopButton onClick={handleCloseCompletionModal} variant="filled">
                                        Add Dependant
                                    </LoopButton>
                                </StyledModularAddOnsCompletionModalContent>
                                <StyledModularAddOnsCompletionModalFam>
                                    <InfoIcon width="20" height="20" strokeColor={theme.colors.secondaryText} />
                                    <Typography variant="small" color="secondary">
                                        If you remove this benefit, it will restore your original structure
                                    </Typography>
                                </StyledModularAddOnsCompletionModalFam>
                            </StyledModularAddOnsCompletionsModalBody>
                        )}
                    </StyledModularAddOnsCloseModal>
                </WithThemeProvider>
            </Modal>
            <Modal
                isVisible={!!isDeletionModalVisible}
                setIsVisible={handleCloseDeletionModal}
                isModalClosable={!isDowngrading || !isDeleting}
                disablePadding
            >
                <WithThemeProvider>
                    <StyledModularAddOnsCloseModal>
                        <img src={ShruggingGraphic} />
                        {isDowngrading ? (
                            <Loader minHeight="100px" />
                        ) : (
                            <>
                                <StyledModularAddOnsCloseModalBody>
                                    <Typography variant="title2" weight="medium">
                                        {deletionPlan?.mode === IPlanDeletionDiffMode.DEL ? 'Removing' : 'Changing'}
                                        &nbsp;
                                        {deletionPlan?.plan?.name} will lead to removal of dependants
                                    </Typography>
                                    <StyledModularAddOnsDeletionsModalContent>
                                        <Typography variant="extraSmall" weight="medium" color="secondary">
                                            Dependants getting removed
                                        </Typography>
                                        {deletionPlan?.deletableDependents?.map((m) => (
                                            <StyledModularDeletableMember key={m.id}>
                                                <ProfileIcon
                                                    icon={getIconMapping(m.relationship, m.gender)}
                                                    bgColor={theme.colors.coverYellow}
                                                />
                                                <StyledModularDeletableMemberText>
                                                    <Typography variant="medium" weight="semiBold">
                                                        {formatFullName(m.firstName, m.lastName)}
                                                    </Typography>
                                                    <Typography variant="extraSmall" weight="medium" color="secondary">
                                                        {capitalizeFirstLetter(m.relationship)}
                                                    </Typography>
                                                </StyledModularDeletableMemberText>
                                            </StyledModularDeletableMember>
                                        ))}
                                    </StyledModularAddOnsDeletionsModalContent>
                                    <StyledModularAddOnsCompletionModalFam>
                                        <InfoIcon width="20" height="20" strokeColor={theme.colors.secondaryText} />
                                        <Typography variant="small" color="secondary">
                                            Your premium will decrease from {deletionPlan?.prevPremium} to&nbsp;
                                            <Typography variant="small" color="secondary" weight="semiBold">
                                                {deletionPlan?.newPremium}
                                            </Typography>
                                            .
                                        </Typography>
                                    </StyledModularAddOnsCompletionModalFam>
                                </StyledModularAddOnsCloseModalBody>
                                <StyledModularAddOnsCloseModalFooter>
                                    <LoopButton onClick={handleCloseDeletionModal} variant="secondary">
                                        Never Mind
                                    </LoopButton>
                                    <LoopButton
                                        onClick={confirmRemoveDependents}
                                        variant="filled"
                                        isLoading={isDeleting}
                                    >
                                        Confirm & Remove
                                    </LoopButton>
                                </StyledModularAddOnsCloseModalFooter>
                            </>
                        )}
                    </StyledModularAddOnsCloseModal>
                </WithThemeProvider>
            </Modal>
            <BenefitDetailsModal benefit={readMorePlan} isVisible={!!readMorePlan} setIsVisible={closeReadMoreModal} />
        </>
    );
};

export default ModularAddOnsModal;
